import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router'; // <-- Agregué RouterModule aquí también
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common'; // <-- ¡VERIFICA ESTA IMPORTACIÓN Y QUE ESTÉ EN 'imports' ABAJO!

import { Survey, Question } from '../shared/models/survey';
import { SurveyService } from '../service/Survey.service';
import { SurveyResponse } from '../shared/models/survey-response.interface'; // Importar la interfaz de respuesta
import { SurveyResponseStatus } from '../shared/models/survey-response-status.enum'; // Importar el enum de estados

import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs'; // Necesario para simular una respuesta asíncrona


@Component({
  selector: 'app-survey',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './survey.component.html',
  styleUrl: './survey.component.scss'
})
export class SurveyComponent implements OnInit {

  survey: Survey | null = null;
  surveyForm!: FormGroup;
  isSubmitting: boolean = false;
  submissionMessage: string | null = null; // Para mostrar mensajes al usuario

  constructor(
    private fb: FormBuilder,
    private surveyService: SurveyService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadSurvey();
  }

  loadSurvey(): void {
    this.route.paramMap.pipe(
      switchMap(params => {
        const surveyId = params.get('id');
        if (surveyId) {
          // Si hay un ID en la URL, intenta cargar la encuesta desde el servicio
          // Si el servicio devuelve null (encuesta no encontrada), se maneja en el .subscribe
          return this.surveyService.getSurveyById(surveyId);
        } else {
          // Si no hay ID en la URL, carga directamente la encuesta por defecto
          console.warn('No se proporcionó un ID de encuesta en la URL. Cargando encuesta por defecto.');
          return of(this.getDefaultSurvey()); // 'of' convierte el objeto en un Observable
        }
      })
    ).subscribe({
      next: (data) => {
        if (data) {
          // Si se encontró la encuesta (ya sea la del ID o la por defecto inicial)
          this.survey = data;
          this.buildSurveyForm();
        } else {
          // Si se proporcionó un ID pero la encuesta no fue encontrada por el servicio (data es null)
          console.error('Encuesta no encontrada para el ID proporcionado. Cargando encuesta por defecto.');
          this.survey = this.getDefaultSurvey(); // Carga la encuesta por defecto
          this.buildSurveyForm();
          this.submissionMessage = 'La encuesta solicitada no fue encontrada. Se ha cargado el formulario de contacto por defecto.';
        }
      },
      error: (err) => {
        // Si hubo un error al cargar la encuesta (ej. problema de red, error del servidor)
        console.error('Error al cargar la encuesta:', err);
        this.survey = this.getDefaultSurvey(); // Carga la encuesta por defecto en caso de error
        this.buildSurveyForm();
        this.submissionMessage = 'Error al cargar la encuesta. Por favor, verifica tu conexión o inténtalo de nuevo. Se ha cargado el formulario de contacto por defecto.';
      }
    });
  }

  /**
   * Genera una encuesta por defecto con campos predefinidos.
   */
  private getDefaultSurvey(): Survey {
    return {
      id: 'contacto-default',
      title: 'Formulario de Contacto',
      description: 'Por favor, completa este formulario para ponerte en contacto con nosotros.',
      questions: [
        {
          id: 'nombre',
          text: 'Tu Nombre Completo',
          type: 'text',
          required: true
        },
        {
          id: 'telefono',
          text: 'Número de Teléfono',
          type: 'text',
          required: false // O true si es obligatorio
        },
        {
          id: 'correo',
          text: 'Correo Electrónico',
          type: 'text',
          required: true
        },
        {
          id: 'mensaje',
          text: 'Tu Mensaje',
          type: 'textarea',
          required: true
        }
      ]
    };
  }

  buildSurveyForm(): void {
    if (!this.survey) {
      return;
    }

    const formControls: { [key: string]: FormControl | FormGroup } = {};

    this.survey.questions.forEach(question => {
      const validators = [];
      if (question.required) { // Ahora 'required' existe en la interfaz Question
        validators.push(Validators.required);
      }
      if (question.id === 'correo') { // Añadir validación de email si el id es 'correo'
        validators.push(Validators.email);
      }

      if (question.type === 'checkbox') {
        const checkboxGroup: { [key: string]: FormControl } = {};
        question.options?.forEach(option => {
          checkboxGroup[option.value] = new FormControl(false);
        });
        formControls[question.id] = this.fb.group(checkboxGroup);
      } else {
        formControls[question.id] = new FormControl('', validators); // Aplica validadores aquí
      }
    });

    this.surveyForm = this.fb.group(formControls);
  }

  onSubmit(): void {
    this.submissionMessage = null; // Limpiar mensajes anteriores

    if (this.surveyForm.valid && this.survey) {
      this.isSubmitting = true;

      const rawFormValues = this.surveyForm.value;
      const responses: { [key: string]: { questionText: string; answer: any } } = {};

      this.survey.questions.forEach(question => {
        const questionId = question.id;
        const questionText = question.text;
        let answer: any;

        if (question.type === 'checkbox') {
          answer = rawFormValues[questionId];
        } else {
          answer = rawFormValues[questionId];
        }

        responses[questionId] = {
          questionText: questionText,
          answer: answer
        };
      });

      const submissionPayload: SurveyResponse = {
        surveyId: this.survey.id,
        surveyTitle: this.survey.title,
        surveyDescription: this.survey.description,
        responses: responses,
        status: SurveyResponseStatus.NEW
      };


      this.surveyService.submitSurveyResponse(submissionPayload).subscribe({
        next: (response) => {
          this.submissionMessage = '¡Gracias! Tu información ha sido enviada con éxito.';
          this.surveyForm.reset(); // Limpia el formulario
        },
        error: (err) => {
          console.error('Error al enviar la encuesta:', err);
          this.submissionMessage = 'Hubo un error al enviar tu información. Por favor, inténtalo de nuevo.';
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
    } else {
      console.warn('El formulario no es válido o la encuesta no está cargada. Revisa los campos.');
      this.surveyForm.markAllAsTouched(); // Marca todos los campos como tocados para mostrar errores de validación
      this.submissionMessage = 'Por favor, completa todos los campos requeridos y verifica que sean válidos.';
    }
  }

  getCheckboxGroup(questionId: string): FormGroup {
    return this.surveyForm.get(questionId) as FormGroup;
  }

  getQuestionControl(questionId: string): FormControl {
    return this.surveyForm.get(questionId) as FormControl;
  }

  /**
   * Helper function for placeholder text based on question ID.
   * Moved from HTML script tag to component.
   */
  getPlaceholder(questionId: string): string {
    switch (questionId) {
      case 'nombre':
        return 'Escribe tu nombre completo aquí...';
      case 'telefono':
        return 'Ej. 55 1234 5678';
      case 'correo':
        return 'ejemplo@dominio.com';
      case 'mensaje':
        return 'Comparte tus comentarios o preguntas aquí...';
      default:
        return 'Escribe tu respuesta aquí...';
    }
  }
}
